import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Col,
  Flex,
  Form,
  Input,
  Modal,
  ModalProps,
  Row,
  Select
} from 'antd'
import { UploadFile } from 'antd/lib'
import { TFunction } from 'i18next'
import { useEffect } from 'react'

import {
  useReportsServiceCreateReportEndpoint,
  useReportsServiceGetReportEndpoint,
  useReportsServiceGetReportEndpointKey,
  useReportsServiceSearchReportsEndpointKey,
  useReportsServiceUpdateReportEndpoint
} from 'src/api/queries'
import {
  ApiError,
  ClassifierCategory,
  ClassifierType,
  ReportSystemDataSource
} from 'src/api/requests'
import { LANGUAGE_KEYS, requiredRules } from 'src/app-constants'
import {
  ClassifierInfiniteSelect,
  CustomItemRender,
  message,
  RoleSelect,
  Upload
} from 'src/components'
import { onDownloadFile, useClassifierId, useFileHandler } from 'src/hooks'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

type Props = {
  onCancel: () => void
  id?: string
} & ModalProps

const getModalText = (isEdit: boolean, t: TFunction) => {
  return {
    title: isEdit
      ? t('reports_administration.edit_report')
      : t('reports_administration.add_report'),
    button: isEdit ? t('common.edit') : t('common.add')
  }
}

export const ReportModal = ({ onCancel, id, ...props }: Props) => {
  const { t } = useLocales()

  const { title, button } = getModalText(!!id, t)

  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  const { onFileChange, saveFiles } = useFileHandler({
    form,
    folder: id ? `reports-administrations-${id}` : 'reports-administrations'
  })

  const categoryClassifierId = useClassifierId(
    ClassifierType.REPORT_CATEGORY,
    ClassifierCategory.REPORTS
  )

  const { data, isLoading } = useReportsServiceGetReportEndpoint(
    {
      id: String(id)
    },
    [id],
    { enabled: !!id }
  )

  const onMutationSuccess = (messageText: string) => {
    queryClient.invalidateQueries({
      queryKey: [useReportsServiceSearchReportsEndpointKey]
    })
    queryClient.invalidateQueries({
      queryKey: [useReportsServiceGetReportEndpointKey]
    })

    message.success(messageText)
    onCancel()
  }

  const { mutate: create, isPending: isCreating } =
    useReportsServiceCreateReportEndpoint({
      onError: (error: ApiError) => formErrorHandler(error.body.errors),
      onSuccess: () =>
        onMutationSuccess(t('reports_administration.report_created'))
    })

  const { mutate: edit, isPending: isEditing } =
    useReportsServiceUpdateReportEndpoint({
      onError: (error: ApiError) => formErrorHandler(error.body.errors),
      onSuccess: () =>
        onMutationSuccess(t('reports_administration.report_updated'))
    })

  const onSave = async () => {
    const values = await form.validateFields()

    const updatedFiles = await saveFiles()

    if (id) {
      edit({
        id: String(id),
        requestBody: {
          ...values,
          mediaItemId: updatedFiles[0]
        }
      })
    } else {
      create({
        requestBody: {
          ...values,
          mediaItemId: updatedFiles[0]
        }
      })
    }
  }

  const dataSourceOptions = Object.values(ReportSystemDataSource)
    .map((value) => ({
      label: t(`reports_administration.type.${value}`),
      value
    }))
    .filter((option) => option.value !== ReportSystemDataSource.NONE)

  useEffect(() => {
    if (data && id) {
      form.setFieldsValue({
        ...data,
        roleIds: data?.roles?.map((role) => role.id),
        categoryId: data?.category?.id
      })
    }
  }, [data, id])

  return (
    <Modal
      open
      maskClosable={false}
      onCancel={onCancel}
      footer={null}
      title={title}
      width={800}
      loading={isLoading}
      {...props}
    >
      <Flex vertical gap={24}>
        <Form form={form} layout="vertical">
          <Row gutter={[24, 24]}>
            {LANGUAGE_KEYS.map((langKey) => {
              return (
                <Col span={12} key={langKey}>
                  <Form.Item
                    name={['title', langKey]}
                    label={`${t('organizational_structure.equipment_tab.name')} ${langKey.toUpperCase()}`}
                    rules={requiredRules}
                  >
                    <Input placeholder={t('common.enter_text')} />
                  </Form.Item>
                </Col>
              )
            })}

            {categoryClassifierId && (
              <Col span={12}>
                <Form.Item
                  name="categoryId"
                  label={t('classifiers.category')}
                  rules={requiredRules}
                >
                  <ClassifierInfiniteSelect
                    id={categoryClassifierId}
                    placeholder={t('classifiers.category')}
                  />
                </Form.Item>
              </Col>
            )}

            <Col span={12}>
              <Form.Item
                name="dataSource"
                label={t('common.type')}
                rules={requiredRules}
              >
                <Select
                  options={dataSourceOptions}
                  placeholder={t('common.type')}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="roleIds"
                label={t('notification_templates.roles')}
                rules={requiredRules}
              >
                <RoleSelect
                  hasDefault={false}
                  mode="multiple"
                  placeholder={t('notification_templates.roles')}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="description" label={t('common.description')}>
                <Input.TextArea placeholder={t('common.enter_text')} rows={4} />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="docs"
                className="full-width"
                label={t('stock_applications.uploaded_documents')}
                rules={requiredRules}
              >
                <div id="docs-wrapper">
                  <Upload
                    defaultFileList={
                      data?.mediaItem
                        ? [
                            {
                              uid: String(data?.mediaItem?.id),
                              name: String(data?.mediaItem?.name)
                            }
                          ]
                        : []
                    }
                    onChange={onFileChange}
                    customRequest={() => null}
                    maxCount={1}
                    itemRender={(_, file: UploadFile, __, { remove }) => {
                      const isExistingFile = data?.mediaItem?.id === file?.uid

                      return (
                        <CustomItemRender
                          file={file}
                          onRemove={remove}
                          onDownload={
                            isExistingFile
                              ? () => onDownloadFile(t, file?.uid, file?.name)
                              : undefined
                          }
                        />
                      )
                    }}
                  />
                </div>
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <Flex justify="center" gap={8}>
          <Button onClick={onCancel}>{t('common.cancel')}</Button>
          <Button
            type="primary"
            onClick={onSave}
            loading={isCreating || isEditing}
          >
            {button}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  )
}
